import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Field } from '../../../ui';
import './formFields.css';

// Labelled inputs for the ui form store (src/ui/form.jsx), styled with the pk- site kit.
// Errors and hints are linked to the input with aria-describedby.

const describedBy = (...ids) => ids.filter(Boolean).join(' ') || undefined;

const FieldShell = ({ id, label, optional, hint, error, children, labelAside }) => (
  <div className="pk-field">
    <div className="pk-field__top">
      <label className="pk-label" htmlFor={id}>
        {label}
        {optional && <span className="pk-optional"> (optional)</span>}
      </label>
      {labelAside}
    </div>
    {children}
    {error && (
      <p className="pk-error" id={`${id}-error`}>
        {error}
      </p>
    )}
    {hint && (
      <p className="pk-hint" id={`${id}-hint`}>
        {hint}
      </p>
    )}
  </div>
);

/**
 * <TextField name="email" label="Email" type="email" autoComplete="email" rules={[…]} />
 * `after` renders next to the input (e.g. a Verify button); `onValueChange` sees every edit.
 */
export const TextField = ({
  name,
  label,
  rules,
  hint,
  optional,
  labelAside,
  after,
  onValueChange,
  normalize,
  ...inputProps
}) => (
  <Field name={name} rules={rules} normalize={normalize} noStyle>
    {(control, { error }) => {
      const input = (
        <input
          className="pk-input"
          {...inputProps}
          {...control}
          aria-describedby={describedBy(error && `${control.id}-error`, hint && `${control.id}-hint`)}
          onChange={e => {
            control.onChange(e);
            onValueChange?.(e.target.value);
          }}
        />
      );
      return (
        <FieldShell
          id={control.id}
          label={label}
          optional={optional}
          hint={hint}
          error={error}
          labelAside={labelAside}
        >
          {after ? (
            <div className="pk-field__row">
              {input}
              {after}
            </div>
          ) : (
            input
          )}
        </FieldShell>
      );
    }}
  </Field>
);

/** Password input with a show/hide toggle. */
export const PasswordField = ({ name, label, rules, hint, labelAside, autoComplete, ...inputProps }) => {
  const [visible, setVisible] = useState(false);

  return (
    <Field name={name} rules={rules} noStyle>
      {(control, { error }) => (
        <FieldShell id={control.id} label={label} hint={hint} error={error} labelAside={labelAside}>
          <div className="pk-password">
            <input
              className="pk-input"
              {...inputProps}
              {...control}
              type={visible ? 'text' : 'password'}
              autoComplete={autoComplete}
              autoCapitalize="none"
              spellCheck={false}
              aria-describedby={describedBy(error && `${control.id}-error`, hint && `${control.id}-hint`)}
            />
            <button
              type="button"
              className="pk-password__toggle"
              onClick={() => setVisible(v => !v)}
              aria-label={visible ? 'Hide password' : 'Show password'}
              aria-pressed={visible}
              aria-controls={control.id}
            >
              {visible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
        </FieldShell>
      )}
    </Field>
  );
};

// Same rule as the server (Modules/Accounts PasswordPolicy): 8–128 characters,
// at least one letter and one number.
export const PASSWORD_HINT = 'At least 8 characters, with a letter and a number.';

export const newPasswordRules = [
  { required: true, message: 'Choose a password.' },
  { min: 8, message: 'Use at least 8 characters.' },
  { max: 128, message: 'Use 128 characters or fewer.' },
  {
    validator: (_, value) =>
      !value || (/\p{L}/u.test(value) && /\p{Nd}/u.test(value))
        ? Promise.resolve()
        : Promise.reject(new Error('Include at least one letter and one number.')),
  },
];

/** Rule for a "confirm password" field that must equal `otherField`. */
export const matchesField = (otherField, message = "The passwords don't match.") => ({ getFieldValue }) => ({
  validator: (_, value) =>
    !value || value === getFieldValue(otherField) ? Promise.resolve() : Promise.reject(new Error(message)),
});

// Accepts "octocat", "@octocat" or a pasted "https://github.com/octocat" and returns "octocat".
export const normalizeGithubInput = value => {
  let text = String(value || '').trim();
  const fromUrl = text.match(/github\.com\/([^/?#\s]+)/i);
  if (fromUrl) text = fromUrl[1];
  return text.replace(/^@+/, '').replace(/\/+$/, '');
};

export const GITHUB_USERNAME_PATTERN = /^[a-zA-Z0-9](?:[a-zA-Z0-9]|-(?=[a-zA-Z0-9])){0,38}$/;
